import React, { useEffect, useState, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { grafikService } from '../../../services/grafikService';
import {
  formatMonthYear, getMonthsList, parseShiftTime, pl, shortLocationCode
} from '../../../utils/grafikHelpers';

/**
 * Widok zakladki "Plan vs Realizacja".
 *
 * Props (kontrolowane z GrafikTab):
 *   year, month, locationId
 *   onYearChange, onMonthChange, onLocationChange
 *   locations: array
 */
const OCENA_META = {
  punktualny: { label: 'Punktualny', color: 'var(--success)', icon: 'fa-check', bg: 'rgba(40,167,69,0.06)' },
  spozniony: { label: 'Spozniony', color: 'var(--danger)', icon: 'fa-exclamation-triangle', bg: 'rgba(220,53,69,0.06)' },
  wczesniejsze_wyjscie: { label: 'Wczesniej wyszedl', color: 'var(--warning)', icon: 'fa-arrow-left', bg: 'rgba(255,193,7,0.06)' },
  nieobecny: { label: 'Nieobecny', color: 'var(--orange)', icon: 'fa-times', bg: 'rgba(253,126,20,0.06)' },
  niezrealizowana: { label: 'Niezrealizowana', color: 'var(--text-muted)', icon: 'fa-minus', bg: 'transparent' }
};

const StatCard = ({ label, value, color, suffix }) => (
  <div style={{
    flex: '1 1 140px',
    padding: '0.75rem 0.9rem',
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderLeft: `3px solid ${color || 'var(--info)'}`,
    borderRadius: 6,
    minWidth: 140
  }}>
    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>
      {label}
    </div>
    <div style={{ fontSize: '1.4rem', color: color || 'var(--text)', fontWeight: 700, lineHeight: 1 }}>
      {value ?? 0}{suffix && <span style={{ fontSize: '0.75rem', marginLeft: 4, color: 'var(--text-muted)' }}>{suffix}</span>}
    </div>
  </div>
);

const Badge = ({ ocena, lateMinutes }) => {
  const meta = OCENA_META[ocena] || OCENA_META.niezrealizowana;
  let label = meta.label;
  if (ocena === 'spozniony' && lateMinutes) label = `Spozn. ${lateMinutes} min`;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.15rem 0.5rem',
      borderRadius: 12,
      fontSize: '0.72rem',
      fontWeight: 600,
      backgroundColor: meta.color,
      color: '#fff'
    }}>
      <i className={`fas ${meta.icon}`} style={{ fontSize: '0.65rem' }} />
      {label}
    </span>
  );
};

const PlanVsRealView = ({
  year, month, locationId,
  onYearChange, onMonthChange, onLocationChange,
  locations = []
}) => {
  const today = new Date();
  const [data, setData] = useState({ shifts: [], stats: {} });
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('data');
  const [sortDir, setSortDir] = useState('desc');
  const [filterOcena, setFilterOcena] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await grafikService.getPlanVsReal({
        year, month,
        locationId: locationId || undefined
      });
      const payload = res?.data || res || {};
      setData({
        shifts: Array.isArray(payload.shifts) ? payload.shifts : [],
        stats: payload.stats || {}
      });
    } catch (err) {
      toast.error(err.message || 'Blad pobierania plan vs real');
      setData({ shifts: [], stats: {} });
    } finally {
      setLoading(false);
    }
  }, [year, month, locationId]);

  useEffect(() => { load(); }, [load]);

  const yearsRange = useMemo(() => {
    const cur = today.getFullYear();
    return [cur - 1, cur, cur + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCsv = async () => {
    try {
      const blob = await grafikService.exportPunktualnoscCsv({
        year, month,
        locationId: locationId || undefined
      });
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const locSuffix = locationId ? `_loc${locationId}` : '_all';
      a.download = `plan_vs_real_${year}_${String(month).padStart(2, '0')}${locSuffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV pobrany');
    } catch (err) {
      toast.error(err.message || 'Blad eksportu CSV');
    }
  };

  const goPrevMonth = () => {
    if (month === 1) {
      onMonthChange && onMonthChange(12);
      onYearChange && onYearChange(year - 1);
    } else {
      onMonthChange && onMonthChange(month - 1);
    }
  };
  const goNextMonth = () => {
    if (month === 12) {
      onMonthChange && onMonthChange(1);
      onYearChange && onYearChange(year + 1);
    } else {
      onMonthChange && onMonthChange(month + 1);
    }
  };

  const sortedRows = useMemo(() => {
    let rows = data.shifts || [];
    if (filterOcena) {
      rows = rows.filter(s => (s.ocena_punktualnosci || 'niezrealizowana') === filterOcena);
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av = a[sortBy];
      let bv = b[sortBy];
      if (sortBy === 'data') {
        av = a.data || '';
        bv = b.data || '';
      } else if (sortBy === 'spoznienie_minut') {
        av = Number(a.spoznienie_minut || 0);
        bv = Number(b.spoznienie_minut || 0);
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data.shifts, sortBy, sortDir, filterOcena]);

  const stats = data.stats || {};

  const btnSecondary = {
    padding: '6px 12px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', cursor: 'pointer',
    fontSize: '12px', fontWeight: 500
  };

  const inputStyle = {
    padding: '6px 10px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text)',
    border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px'
  };

  return (
    <div>
      {/* Pasek filtrow */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem',
        padding: '0.75rem 1rem', backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)', borderRadius: '6px',
        marginBottom: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button onClick={goPrevMonth} style={btnSecondary} title="Poprzedni miesiac">&laquo;</button>
          <select value={month} onChange={(e) => onMonthChange && onMonthChange(Number(e.target.value))} style={inputStyle}>
            {getMonthsList().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select value={year} onChange={(e) => onYearChange && onYearChange(Number(e.target.value))} style={inputStyle}>
            {yearsRange.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={goNextMonth} style={btnSecondary} title="Nastepny miesiac">&raquo;</button>
        </div>

        <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginLeft: '0.5rem' }}>
          {formatMonthYear(year, month)}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginLeft: '0.5rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lokalizacja:</span>
          <select
            value={locationId || ''}
            onChange={(e) => onLocationChange && onLocationChange(e.target.value)}
            style={inputStyle}
          >
            <option value="">— wszystkie —</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.nazwa || l.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Filtr oceny:</span>
          <select value={filterOcena} onChange={(e) => setFilterOcena(e.target.value)} style={inputStyle}>
            <option value="">— wszystkie —</option>
            {Object.entries(OCENA_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: 1 }} />

        <button onClick={handleExportCsv} style={btnSecondary}>
          <i className="fas fa-file-csv" style={{ marginRight: '0.35rem' }} />
          Eksport CSV
        </button>
      </div>

      {/* Stats cards */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.75rem'
      }}>
        <StatCard label="Lacznie zmian" value={stats.total_shifts ?? 0} color="var(--info)" />
        <StatCard label="Zrealizowane" value={stats.realized ?? 0} color="var(--success)" />
        <StatCard label="Punktualnie" value={stats.on_time ?? 0} color="var(--success)" />
        <StatCard label="Spoznienia" value={stats.late ?? 0} color="var(--warning)" />
        <StatCard label="Nieobecnosci" value={stats.absent ?? 0} color="var(--orange)" />
        <StatCard label="Sredni czas spoznienia"
          value={stats.avg_late_minutes != null ? pl(Number(stats.avg_late_minutes)) : '0,00'}
          suffix="min" color="var(--danger)" />
      </div>

      {/* Tabela */}
      <div style={{
        border: '1px solid var(--border)', borderRadius: '6px',
        backgroundColor: 'var(--card-bg)', overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.4rem' }} />
            Ladowanie danych plan vs real...
          </div>
        ) : sortedRows.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
            Brak danych do wyswietlenia.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
                  {[
                    { key: 'user_login', label: 'Pracownik' },
                    { key: 'data', label: 'Data' },
                    { key: 'location_nazwa', label: 'Lok.' },
                    { key: 'plan_godz', label: 'Plan godz.' },
                    { key: 'real_godz', label: 'Real godz.' },
                    { key: 'godziny_planowane', label: 'Plan h' },
                    { key: 'faktyczne_godziny', label: 'Real h' },
                    { key: 'spoznienie_minut', label: 'Spoznienie' },
                    { key: 'wczesniejsze_wyjscie_minut', label: 'Wcz. wyj.' },
                    { key: 'ocena_punktualnosci', label: 'Status' }
                  ].map(col => {
                    const isSortable = ['data', 'user_login', 'spoznienie_minut'].includes(col.key);
                    const active = sortBy === col.key;
                    return (
                      <th key={col.key}
                        onClick={() => {
                          if (!isSortable) return;
                          if (active) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
                          else { setSortBy(col.key); setSortDir('desc'); }
                        }}
                        style={{
                          padding: '0.5rem 0.6rem',
                          textAlign: 'left',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          color: 'var(--text-muted)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.03em',
                          cursor: isSortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}>
                        {col.label}
                        {active && (
                          <i className={`fas fa-sort-${sortDir === 'asc' ? 'up' : 'down'}`}
                            style={{ marginLeft: 4, fontSize: '0.65rem' }} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(s => {
                  const ocena = s.ocena_punktualnosci || 'niezrealizowana';
                  const meta = OCENA_META[ocena] || OCENA_META.niezrealizowana;
                  const planRange = `${parseShiftTime(s.godzina_od)}–${parseShiftTime(s.godzina_do)}`;
                  const realRange = (s.pos_czas_rozpoczecia || s.pos_czas_zakonczenia)
                    ? `${s.pos_czas_rozpoczecia ? parseShiftTime(s.pos_czas_rozpoczecia.slice(11)) : '—'}–${s.pos_czas_zakonczenia ? parseShiftTime(s.pos_czas_zakonczenia.slice(11)) : '—'}`
                    : '—';
                  return (
                    <tr key={s.id} style={{
                      borderBottom: '1px solid var(--border)',
                      backgroundColor: meta.bg
                    }}>
                      <td style={{ padding: '0.4rem 0.6rem', fontWeight: 600 }}>{s.user_login}</td>
                      <td style={{ padding: '0.4rem 0.6rem', whiteSpace: 'nowrap' }}>{s.data}</td>
                      <td style={{ padding: '0.4rem 0.6rem', color: 'var(--text-muted)' }}>
                        {shortLocationCode(s.location_nazwa)}
                      </td>
                      <td style={{ padding: '0.4rem 0.6rem', whiteSpace: 'nowrap' }}>{planRange}</td>
                      <td style={{ padding: '0.4rem 0.6rem', whiteSpace: 'nowrap' }}>{realRange}</td>
                      <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>
                        {pl(Number(s.godziny_planowane || 0))}
                      </td>
                      <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right' }}>
                        {s.faktyczne_godziny != null ? pl(Number(s.faktyczne_godziny)) : '—'}
                      </td>
                      <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: Number(s.spoznienie_minut || 0) > 0 ? 'var(--danger)' : 'var(--text-muted)' }}>
                        {Number(s.spoznienie_minut || 0)} min
                      </td>
                      <td style={{ padding: '0.4rem 0.6rem', textAlign: 'right', color: Number(s.wczesniejsze_wyjscie_minut || 0) > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
                        {Number(s.wczesniejsze_wyjscie_minut || 0)} min
                      </td>
                      <td style={{ padding: '0.4rem 0.6rem' }}>
                        <Badge ocena={ocena} lateMinutes={s.spoznienie_minut} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanVsRealView;
